import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ExclamationCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PlusIcon,
  MinusIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../utils/supabase';
import { inventoryApi } from '../../utils/api'; // Ajuster le chemin si nécessaire
// Indicateur d'activation de l'intégration Shopify (REACT_APP_ENABLE_SHOPIFY)
import { SHOPIFY_ENABLED } from '../../config/features';

const StockManagement = () => {
  // États pour les données et les filtres
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'low', 'out'
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [adjustmentQuantities, setAdjustmentQuantities] = useState({});
  const [selectedVariantIds, setSelectedVariantIds] = useState({});
  // Nouveaux états pour les alertes
  const [alertConfigs, setAlertConfigs] = useState({}); // { variantId: { threshold: number, isActive: boolean, loading: boolean, error: string|null } }
  const [editingAlertVariantId, setEditingAlertVariantId] = useState(null); // Pour suivre quelle alerte est en cours de sauvegarde
  const [syncingProductId, setSyncingProductId] = useState(null); // État pour le chargement de la synchronisation Shopify par PRODUIT

  // Nouveaux états pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [pageSize, setPageSize] = useState(30); // Fixé à 30 produits par page

  // Nouveau état pour la synchronisation complète
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);



  // Charger les produits depuis l'API backend
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: currentPage,
        pageSize: pageSize,
        searchTerm: searchTerm,
        filter: filter,
        // Ajouter sortConfig si le backend le gère
        // sortKey: sortConfig.key,
        // sortDirection: sortConfig.direction,
      };

      // Utiliser inventoryApi.getProductsForStock
      const response = await inventoryApi.getProductsForStock(params);
      
      if (response && response.data) {
        // Les produits sont déjà formatés par le backend
        setProducts(response.data.products || []);
        setTotalProducts(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 0);
      } else {
        throw new Error("Réponse invalide de l'API");
      }
    } catch (error) {
      console.error('Erreur lors du chargement des produits depuis lAPI:', error);
      setError(error.response?.data?.message || error.message || 'Erreur de chargement des produits.');
      setProducts([]); // Réinitialiser les produits en cas d'erreur
      setTotalProducts(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, filter, /* sortConfig */]); // Ajouter sortConfig si le tri est géré par le backend

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]); // fetchProducts inclut maintenant currentPage, searchTerm, filter

  // Gérer le changement de page
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Appliquer les filtres et recherches (devient moins pertinent si le backend gère tout)
  // Si le backend gère le tri et le filtrage, cette section peut être simplifiée ou retirée.
  // Pour l'instant, on la garde, car le backend filtre par searchTerm mais pas encore dynamiquement par 'status' calculé.
  const filteredProducts = useMemo(() => {
    // Le filtrage par searchTerm est déjà fait par le backend.
    // Le filtrage par 'filter' (low, out) est aussi fait par le backend.
    // Le tri via 'sortConfig' est actuellement géré côté client.
    // Si on veut un tri backend, il faudrait l'ajouter à la requête API et ajuster ici.
    return products; // Les produits sont déjà filtrés par le backend selon searchTerm et filter (status)
  }, [products]);

  // Trier les produits
  const sortedProducts = useMemo(() => {
    let sortableItems = [...filteredProducts];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredProducts, sortConfig]);

  // Gérer le tri
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Fonction utilitaire pour tronquer le texte
  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Mettre à jour le stock
  const updateStock = async (productId, newStock, variantId = null) => {
    try {
      if (variantId) {
        const { error } = await supabase
          .from('product_variants')
          .update({ inventory_quantity: newStock })
          .eq('id', variantId);
          
        if (error) throw error;
        
        setProducts(prevProducts => prevProducts.map(product => {
          if (product.id === productId) {
            const newVariants = product.variants.map(variant => 
              variant.id === variantId ? {...variant, stock: newStock} : variant
            );
            const newStockLevel = newVariants.reduce((sum, variant) => sum + variant.stock, 0);
            return {
              ...product,
              variants: newVariants,
              stockLevel: newStockLevel,
              status: newStockLevel <= 0 ? 'out' : newStockLevel < 5 ? 'low' : 'ok'
            };
          }
          return product;
        }));
      } else {
        const { error } = await supabase
          .from('products')
          .update({ stock_level: newStock })
          .eq('id', productId);
          
        if (error) throw error;
        
        setProducts(prevProducts => prevProducts.map(product => 
          product.id === productId ? {
            ...product, 
            stockLevel: newStock,
            status: newStock <= 0 ? 'out' : newStock < 5 ? 'low' : 'ok'
          } : product
        ));
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du stock:', error);
      alert(`Erreur: ${error.message}`);
    }
  };
  
  // **NOUVELLE FONCTION : Charger la configuration d'alerte pour une variante**
  const fetchAlertConfig = async (variantId) => {
    if (!variantId || alertConfigs[variantId]?.threshold !== undefined) { // Ne pas recharger si déjà présent
      // console.log(`Config alerte pour ${variantId} déjà chargée ou ID invalide.`);
      return;
    }

    console.log(`Chargement config alerte pour variante ${variantId}...`);
    setAlertConfigs(prev => ({ 
      ...prev, 
      [variantId]: { ...prev[variantId], loading: true, error: null } 
    }));

    try {
      // Note: L'API actuelle ne permet pas de récupérer une seule alerte facilement par variantId
      // On doit récupérer toutes les alertes et filtrer, ou créer une nouvelle route API.
      // Pour l'instant, on simule une récupération avec des valeurs par défaut.
      // ** A FAIRE: Implémenter une vraie route API GET /api/inventory/alert/:variantId **
      // ou modifier GET /api/inventory/alerts pour filtrer par variantId.
      
      // --- Simulation / Placeholder --- 
      // Remplacer ceci par un appel API réel plus tard
       const { data: alerts, error: alertsError } = await supabase
           .from('inventory_alerts')
           .select('id, threshold, is_active')
           .eq('product_variant_id', variantId)
           .maybeSingle(); // Utiliser maybeSingle pour gérer le cas où aucune alerte n'existe
       
       if (alertsError) throw alertsError;
      
       let config = { threshold: 5, isActive: false }; // Valeurs par défaut si aucune alerte trouvée
       if (alerts) { // Si une alerte existe
           config = { threshold: alerts.threshold, isActive: alerts.is_active };
       }
      // --- Fin Simulation --- 

      setAlertConfigs(prev => ({
        ...prev,
        [variantId]: { 
          ...config, // Utiliser les données récupérées ou les défauts
          loading: false, 
          error: null 
        }
      }));
      console.log(`Config alerte chargée pour ${variantId}:`, config);

    } catch (error) {
      console.error(`Erreur chargement config alerte pour ${variantId}:`, error);
      setAlertConfigs(prev => ({ 
        ...prev, 
        [variantId]: { ...prev[variantId], loading: false, error: error.message }
      }));
    }
  };

  // **NOUVELLE FONCTION : Gérer le changement de la config d'alerte (seuil, active)**
  const handleAlertConfigChange = (variantId, field, value) => {
      setAlertConfigs(prev => ({
          ...prev,
          [variantId]: {
              ...(prev[variantId] || { threshold: 5, isActive: false }), // Assurer que l'objet existe
              [field]: value,
              error: null // Effacer l'erreur précédente lors de l'édition
          }
      }));
  };

  // **NOUVELLE FONCTION : Sauvegarder la configuration d'alerte**
  const handleSaveAlertConfig = async (variantId) => {
      const config = alertConfigs[variantId];
      // Validation plus stricte pour threshold
      if (!config || config.threshold === undefined || config.threshold === '' || isNaN(Number(config.threshold)) || Number(config.threshold) < 0 || typeof config.isActive !== 'boolean') {
          alert('Configuration d\'alerte invalide. Le seuil doit être un nombre positif ou zéro.');
          return;
      }

      setEditingAlertVariantId(variantId); // Indiquer le début de la sauvegarde
      setAlertConfigs(prev => ({ 
          ...prev, 
          [variantId]: { ...config, loading: true, error: null } 
      }));

      try {
          // Utiliser l'API configurée
          const response = await inventoryApi.setupStockAlert({ 
              variantId: variantId, 
              threshold: Number(config.threshold), 
              isActive: config.isActive 
          });
          console.log('response', response);
          // axios retourne les données directement dans response.data
          const result = response.data; 

          // L'intercepteur axios gère les erreurs réseau/status non-2xx 
          // On vérifie juste la réponse logique du backend si nécessaire
          if (!result || result.success === false) {
              throw new Error(result?.message || `Erreur lors de la sauvegarde`);
          }

          // Mettre à jour l'état local avec succès
          setAlertConfigs(prev => ({
              ...prev,
              [variantId]: { ...config, threshold: Number(config.threshold), loading: false, error: null } // Stocker le seuil comme nombre
          }));
          console.log(`Configuration d'alerte sauvegardée pour ${variantId}:`, result);
          alert("Configuration d'alerte sauvegardée !");

      } catch (error) {
          console.error(`Erreur sauvegarde config alerte pour ${variantId}:`, error);
          setAlertConfigs(prev => ({ 
              ...prev, 
              [variantId]: { ...config, loading: false, error: error.message }
          }));
          alert(`Erreur sauvegarde: ${error.message}`);
      } finally {
          setEditingAlertVariantId(null); // Fin de la sauvegarde
      }
  };

  // Gérer le changement de quantité d'ajustement (input onChange)
  // Utilise maintenant les clés standardisées
  const handleAdjustmentChange = (key, value) => {
    // Permettre la saisie de nombres négatifs temporairement si besoin, mais updateStock limitera à 0
    const quantity = value === '-' ? '-' : parseInt(value, 10); 
    setAdjustmentQuantities(prev => ({
        ...prev,
        // Stocker le nombre ou une chaîne vide, ou '-'
        [key]: value === '' ? '' : (isNaN(quantity) ? adjustmentQuantities[key] || '' : quantity) 
    }));
  };
  
  // Gérer le clic sur les boutons +/- : Modifie SEULEMENT l'input
  // Utilise maintenant les clés standardisées
  const handleAdjustStockClick = (productId, adjustment, variantId = null) => {
    const key = variantId ? `variant-${variantId}` : `product-${productId}`;
    const product = products.find(p => p.id === productId);
    if (!product) return;

    let currentStock;
    if (variantId) {
        const variant = product.variants.find(v => v.id === variantId);
        if (!variant) return;
        currentStock = variant.stock;
    } else {
        // Vérifier si on doit utiliser le stock du produit (pas de variations actives)
        const activeVariants = product.variants || [];
         if (activeVariants.length > 0) {
             console.warn("Using +/- on main product with active variants. Input might be misleading.");
             // Idéalement, désactiver les boutons +/- pour le produit si variations existent
             // Pour l'instant, on se base sur product.stockLevel comme référence
         }
        currentStock = product.stockLevel;
    }
    
    // Lire la valeur actuelle de l'input OU le stock réel si l'input est vide/invalide
    const currentInputValue = adjustmentQuantities[key];
    let baseValue;
    if (currentInputValue !== '' && !isNaN(Number(currentInputValue))) {
      baseValue = Number(currentInputValue);
    } else {
      baseValue = currentStock;
    }

    const potentialNewTotalStock = baseValue + adjustment;

    setAdjustmentQuantities(prev => ({
        ...prev,
        [key]: Math.max(0, potentialNewTotalStock) // Mettre à jour l'input avec le nouveau total potentiel (min 0)
    }));
  };

  // Gérer le changement de variation sélectionnée
  // Réinitialise aussi le champ d'ajustement associé en utilisant la clé standardisée
  const handleVariantSelect = (productId, variantId) => {
      setSelectedVariantIds(prev => ({
          ...prev,
          [productId]: variantId
      }));
      // Réinitialiser la quantité d'ajustement pour la nouvelle variante sélectionnée
      const key = `variant-${variantId}`; // Clé de la nouvelle variante
      const oldKeyPrefix = `variant-`; // Pour trouver l'ancienne clé potentielle
      
      // **Ajout : Charger la config d'alerte pour la variante sélectionnée**
      if (variantId) { // Seulement si un ID valide est sélectionné
          fetchAlertConfig(variantId);
      }

      setAdjustmentQuantities(prev => {
          const newState = {...prev};
          // Optionnel: supprimer l'ancienne valeur de variation pour ce produit si on veut
          Object.keys(newState).forEach(k => {
              if (k.startsWith(oldKeyPrefix) && products.find(p => p.id === productId)?.variants.some(v => `${oldKeyPrefix}${v.id}` === k)) {
                 // delete newState[k]; // Ou garder l'ancienne valeur ? Pour l'instant on la garde.
              }
          });
          newState[key] = ''; // Réinitialiser la nouvelle clé
          return newState;
      });
  };
  
  // NOUVELLE FONCTION: Définir le stock à la valeur de l'input
  // Remplace handleApplyManualAdjustment et est appelée par le bouton "Définir Stock à"
  const handleSetStock = (productId, variantId = null) => {
    const key = variantId ? `variant-${variantId}` : `product-${productId}`;
    const valueToSet = adjustmentQuantities[key];

    // Vérifier si la valeur est un nombre valide (et non vide)
    if (valueToSet === '' || valueToSet === undefined || isNaN(Number(valueToSet))) {
        console.warn("Value to set is empty or not a valid number for key:", key, "Value:", valueToSet);
        // Optionnel: Réinitialiser explicitement si invalide ? Ou juste ignorer.
        // setAdjustmentQuantities(prev => ({ ...prev, [key]: '' })); 
        alert("Veuillez entrer une valeur numérique valide pour le stock.");
        return;
    }

    const numericValueToSet = Math.max(0, Number(valueToSet)); // Assurer que le stock n'est pas négatif

    const product = products.find(p => p.id === productId);
    if (!product) {
        console.error("Product not found for ID:", productId);
        return;
    }

    // Vérification de sécurité : ne pas définir le stock du produit s'il a des variations actives
    if (!variantId) {
        const activeVariants = product.variants || [];
        if (activeVariants.length > 0) {
             console.warn("Attempting to set main product stock when active variants exist. Product ID:", productId);
             alert("Le stock est géré par les variations actives. Veuillez modifier le stock d'une variation.");
             setAdjustmentQuantities(prev => ({ ...prev, [key]: '' })); // Réinitialiser le champ
             return; 
        }
    }
    
    // Utiliser async/await pour gérer la promesse de updateStock
    const applyUpdate = async () => {
      try {
        // Appeler updateStock avec la valeur comme NOUVEAU TOTAL
        await updateStock(productId, numericValueToSet, variantId); 
        // Réinitialiser le champ APRÈS la mise à jour réussie
        setAdjustmentQuantities(prev => ({
            ...prev,
            [key]: '' // Réinitialiser à une chaîne vide
        }));
      } catch (error) {
        console.error("Failed to update stock, adjustment field not reset.", error);
        alert(`Erreur lors de la mise à jour du stock: ${error.message}.`);
      }
    }
    
    applyUpdate();
  };

  const handleToggleExpand = (productId) => {
    const currentlyExpanded = expandedProduct === productId;
    const newExpandedId = currentlyExpanded ? null : productId;
    setExpandedProduct(newExpandedId);

    // Si on ouvre une nouvelle ligne ET qu'elle a exactement une variante
    if (newExpandedId) {
      const product = products.find(p => p.id === newExpandedId);
      if (product && product.variants && product.variants.length === 1) {
        const singleVariantId = product.variants[0].id;
        // Auto-sélectionner la variante et charger sa config d'alerte
        handleVariantSelect(productId, singleVariantId);
      }
      // Si on a plus d'une variante, on ne présélectionne pas
      // Si on a zéro variante (produit simple), on n'appelle pas handleVariantSelect
    }
  };

  // **MODIFIÉE : Synchroniser le stock d'un PRODUIT Shopify et toutes ses variantes**
  const handleSyncShopifyStock = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) {
      alert("Produit non trouvé.");
      return;
    }
    // Vérifier si le produit est bien de source Shopify
    // On pourrait aussi vérifier s'il a un product.external_id si c'est nécessaire pour l'API.
    if (product.external_source !== 'shopify') {
      alert("Ce produit n'est pas géré par Shopify.");
      return;
    }

    setSyncingProductId(productId);
    try {
      // Note: inventoryApi.syncShopifyProductStock doit être créé/modifié dans client/src/utils/api.js
      // et appeler une route backend comme POST /api/inventory/shopify-sync/product/:productId
      // ou /api/inventory/shopify-sync/product/:productExternalId si le produit a un ID externe.
      // L'API backend se chargera de trouver toutes les variantes associées.
      const response = await inventoryApi.syncShopifyProductStock(productId); 
      
      if (response && response.data && response.data.success) {
        alert(`Stock pour le produit ${product.name} et ses variantes synchronisé avec succès.`);
        // Recharger tous les produits pour refléter les changements.
        await fetchProducts(); 
      } else {
        throw new Error(response?.data?.message || "La synchronisation du produit a échoué. Réponse inattendue du serveur.");
      }
    } catch (error) {
      console.error(`Erreur lors de la synchronisation du stock Shopify pour le produit ${productId}:`, error);
      alert(`Erreur de synchronisation: ${error.message}`);
    } finally {
      setSyncingProductId(null);
    }
  };



  // **NOUVELLE FONCTION : Synchroniser tous les produits Shopify modifiés**
  const handleSyncAllShopify = async () => {
    setIsSyncingAll(true);
    try {
      console.log('[StockManagement] Début de la synchronisation complète Shopify');
      
      const response = await inventoryApi.syncAllShopifyProducts();
      
      if (response && response.data && response.data.success) {
        const data = response.data.data;
        const message = `Synchronisation terminée !
        
 Résumé :
• ${data.totalProductsFound || data.productsProcessed} produits trouvés sur Shopify
• ${data.pagesProcessed || 1} page(s) Shopify traitée(s)
• ${data.productsProcessed} produits traités
• ${data.productsCreated} produits créés
• ${data.productsUpdated} produits mis à jour  
• ${data.variantsCreated} variantes créées
• ${data.variantsUpdated} variantes mises à jour

${data.errors?.length > 0 ? `⚠️ ${data.errors.length} erreur(s) détectée(s)` : '✅ Aucune erreur'}
${data.warnings?.length > 0 ? `⚠️ ${data.warnings.length} avertissement(s)` : ''}`;

        // Stocker les résultats pour affichage dans le modal
        setSyncResults({
          success: true,
          message: response.data.message,
          data: data
        });
        setShowSyncModal(true);
        
        // Recharger la liste des produits pour afficher les nouveaux/modifiés
        await fetchProducts();
        
        console.log('[StockManagement] Synchronisation complète terminée:', data);
      } else {
        throw new Error(response?.data?.message || "La synchronisation complète a échoué. Réponse inattendue du serveur.");
      }
    } catch (error) {
      console.error('[StockManagement] Erreur lors de la synchronisation complète:', error);
      
      // Stocker l'erreur pour affichage dans le modal
      setSyncResults({
        success: false,
        message: `Erreur de synchronisation complète: ${error.message}`,
        data: null
      });
      setShowSyncModal(true);
    } finally {
      setIsSyncingAll(false);
    }
  };

  // La colonne Actions n'existe que si elle peut contenir un bouton (Sync Shopify) :
  // intégration activée ET au moins un produit géré par Shopify dans la liste affichée.
  const showActionsColumn = SHOPIFY_ENABLED && sortedProducts.some((p) => p.external_source === 'shopify');
  // Nombre de colonnes du tableau (pour le colSpan de la ligne dépliée)
  const columnCount = showActionsColumn ? 6 : 5;

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      {/* En-tête avec filtres et recherche */}
      <div className="p-4 border-b border-gray-200 bg-gray-50 grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            placeholder="Rechercher un produit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={isSyncingAll}
          />
        </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md ${filter === 'all' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 border border-gray-300'} disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none`}
                onClick={() => setFilter('all')}
                disabled={isSyncingAll}
              >
                Tous
              </button>
              <button
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md ${filter === 'low' ? 'bg-yellow-100 border border-yellow-400 text-yellow-700' : 'bg-white text-gray-700 border border-gray-300'} disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none`}
                onClick={() => setFilter('low')}
                disabled={isSyncingAll}
              >
                <span className="hidden sm:inline">Stock </span>Bas
              </button>
              <button
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md ${filter === 'out' ? 'bg-red-100 border border-red-400 text-red-700' : 'bg-white text-gray-700 border border-gray-300'} disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none`}
                onClick={() => setFilter('out')}
                disabled={isSyncingAll}
              >
                Rupture
              </button>
            </div>
          
          {/* Bouton Synchro All Shopify : masqué quand l'intégration est désactivée */}
          {SHOPIFY_ENABLED && <button
            onClick={handleSyncAllShopify}
            disabled={isSyncingAll}
            className="px-4 py-2 text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            title="Synchroniser tous les produits Shopify modifiés depuis la dernière synchronisation"
          >
            {isSyncingAll ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                <span>Synchro...</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Synchro All</span>
              </>
            )}
          </button>}
        </div>
      </div>

      {/* Indicateur de chargement */}
      {loading && (
        <div className="p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
          <p className="mt-2 text-gray-500">Chargement des produits...</p>
        </div>
      )}

      {/* Message d'erreur */}
      {error && (
        <div className="p-8 text-center text-red-500">
          <p>Erreur: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-md"
          >
            Réessayer
          </button>
        </div>
      )}

      {/* Tableau de gestion des stocks */}
      {!loading && !error && (
        <div className={`overflow-x-auto ${isSyncingAll ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Sous md : chaque produit devient une carte (thead masqué) ; dès md : tableau classique */}
          <table className="block min-w-full divide-y divide-gray-200 md:table">
            <thead className="hidden bg-gray-50 md:table-header-group">
              <tr>
                {/* Colonne vide alignée sur le bouton d'ajustement de chaque ligne */}
                <th scope="col" className="px-6 py-3 w-12"><span className="sr-only">Ajuster</span></th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => requestSort('name')}
                >
                  <div className="flex items-center">
                    Produit
                    {sortConfig.key === 'name' ? (
                      sortConfig.direction === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" /> : <ChevronDownIcon className="ml-1 h-4 w-4" />
                    ) : null}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => requestSort('category')}
                >
                  <div className="flex items-center">
                    Catégorie
                    {sortConfig.key === 'category' ? (
                      sortConfig.direction === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" /> : <ChevronDownIcon className="ml-1 h-4 w-4" />
                    ) : null}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => requestSort('stockLevel')}
                >
                  <div className="flex items-center">
                    Stock
                    {sortConfig.key === 'stockLevel' ? (
                      sortConfig.direction === 'asc' ? <ChevronUpIcon className="ml-1 h-4 w-4" /> : <ChevronDownIcon className="ml-1 h-4 w-4" />
                    ) : null}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                {showActionsColumn && (
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="block bg-white divide-y divide-gray-200 md:table-row-group">
              {sortedProducts.map((product) => {
                  const isExpanded = expandedProduct === product.id;
                  const selectedVariantId = selectedVariantIds[product.id];
                  const selectedVariant = product.has_variants
                      ? product.variants.find(v => v.id === selectedVariantId)
                      : null;
                  
                  // Clé standardisée pour produit simple
                  const productKey = `product-${product.id}`;
                  // Clé standardisée pour la variation sélectionnée (ou null si aucune)
                  const variantKey = selectedVariantId ? `variant-${selectedVariantId}` : null;
                  // Clé à utiliser pour l'input (soit celle de la variation, soit celle du produit)
                  const adjustmentKey = variantKey || productKey;
                  
                  const adjustmentValue = adjustmentQuantities[adjustmentKey] || '';
                  const isShopifyManaged = product.external_source === 'shopify';

                  return (
                    <React.Fragment key={product.id}>
                      {/* Mobile : carte en grille (produit | bouton d'ajustement), cellules ordonnées via order-* */}
                      <tr className="grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-2 p-4 hover:bg-gray-50 md:table-row md:p-0">
                         {/* Bouton Expand/Collapse */}
                         <td className="order-2 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                            {product.has_variants && ( // Afficher seulement si variations existent
                                <button 
                                    onClick={() => handleToggleExpand(product.id)}
                                    className="text-gray-400 hover:text-gray-600"
                                    title={isExpanded ? "Réduire" : "Ajuster le stock des variations"}
                                >
                                    {isExpanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                                </button>
                             )}
                            {/* Ajouter bouton pour ajuster produit simple ? Ou utiliser expand aussi ? */}
                            {!product.has_variants && (
                               <button 
                                 onClick={() => handleToggleExpand(product.id)}
                                 className="text-gray-400 hover:text-gray-600"
                                 title={isExpanded ? "Réduire" : "Ajuster le stock"}
                                >
                                 {isExpanded ? <ChevronUpIcon className="h-5 w-5" /> : <AdjustmentsHorizontalIcon className="h-5 w-5" />}
                                </button>
                            )}
                          </td>
                         {/* Colonne Produit (Image, Nom, SKU) */}
                         <td className="order-1 min-w-0 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                           <div className="flex items-center">
                             <div className="flex-shrink-0 h-10 w-10">
                                <img className="h-10 w-10 rounded-md object-cover" 
                                     src={selectedVariant?.image_url || product.mainImage} 
                                     alt={product.name} />
                             </div>
                             <div className="ml-4">
                               <div className="text-sm font-medium text-gray-900 flex items-center">
                                 {truncateText(product.name)}
                                 {/* Étiquette d'origine Shopify : masquée quand l'intégration est désactivée */}
                                 {SHOPIFY_ENABLED && product.external_source === 'shopify' && (
                                   <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                     Shopify
                                   </span>
                                 )}
                               </div>
                               <div className="text-sm text-gray-500">{product.sku}</div>
                             </div>
                           </div>
                         </td>
                         {/* Colonne Catégorie */}
                          <td className="order-3 col-span-2 text-sm text-gray-500 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                            {/* Libellés visibles uniquement en mode carte (mobile) */}
                            <span className="text-gray-400 md:hidden">Catégorie : </span>
                            {product.category}
                          </td>
                         {/* Colonne Stock (Total) */}
                         <td className="order-4 text-sm font-medium text-left md:table-cell md:px-6 md:py-4 md:whitespace-nowrap md:text-center">
                            <span className="font-normal text-gray-400 md:hidden">Stock : </span>
                            {product.stockLevel}
                         </td>
                         {/* Colonne Statut */}
                         <td className="order-5 justify-self-end text-sm text-gray-500 md:table-cell md:px-6 md:py-4 md:whitespace-nowrap">
                           <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${product.status === 'ok' ? 'bg-green-100 text-green-800' : product.status === 'low' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                               {product.status === 'ok' ? 'En stock' : product.status === 'low' ? 'Stock bas' : 'Rupture'}
                            </span>
                         </td>
                         {/* Colonne Actions */}
                         {showActionsColumn && <td className="order-6 col-span-2 text-sm text-gray-500 empty:hidden md:table-cell md:px-6 md:py-4 md:whitespace-nowrap md:text-right">
                            {/* Bouton Sync Shopify : masqué quand l'intégration est désactivée */}
                            {SHOPIFY_ENABLED && product.external_source === 'shopify' && (
                                <button
                                  onClick={() => handleSyncShopifyStock(product.id)}
                                  disabled={syncingProductId === product.id || isSyncingAll}
                                  className="px-3 py-1.5 border border-purple-500 text-xs font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                                >
                                  {syncingProductId === product.id ? 'Sync...' : 'Sync Shopify'}
                                </button>
                            )}
                         </td>}
                      </tr>

                      {/* Ligne dépliée pour l'ajustement */}
                      {isExpanded && (
                        <tr className="block md:table-row">
                          {/* Ajuster le colSpan au nombre total de colonnes dans le thead */}
                          <td colSpan={columnCount} className="block px-3 py-4 bg-gray-50 md:table-cell md:px-6">
                            {/* Vérifier la longueur du tableau de variantes */} 
                            {product.variants && product.variants.length > 1 ? ( 
                                // ----- Cas avec variations MULTIPLES ----- 
                                <div className="space-y-4"> 
                                    {/* Sélecteur de variation + ajustement de stock */} 
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2"> 
                                        <select
                                            value={selectedVariantId || ''}
                                            onChange={(e) => handleVariantSelect(product.id, e.target.value)}
                                            className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                                          >
                                            <option value="">-- Sélectionner variation --</option>
                                            {product.variants.map(variant => (
                                                <option key={variant.id} value={variant.id}>
                                                    {variant.name} (Actuel: {variant.stock})
                                                </option>
                                            ))}
                                          </select>

                                          {(() => {
                                              const selectedVariantIsShopify = selectedVariant && selectedVariant.external_id && product.external_source === 'shopify';
                                              return (
                                                  <div className={`flex items-center border rounded-md ${!selectedVariant ? 'opacity-50' : ''} ${selectedVariantIsShopify ? 'bg-gray-100 cursor-not-allowed' : ''}`}>
                                                     <button
                                                         onClick={() => selectedVariant && handleAdjustStockClick(product.id, -1, selectedVariant.id)}
                                                         disabled={!selectedVariant || selectedVariantIsShopify}
                                                         className="px-3 py-2 border-r text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                      >
                                                         <MinusIcon className="h-4 w-4" />
                                                      </button>
                                                      <input
                                                         type="number"
                                                         value={adjustmentQuantities[`variant-${selectedVariantId}`] || ''} 
                                                         onChange={(e) => selectedVariantId && handleAdjustmentChange(`variant-${selectedVariantId}`, e.target.value)}
                                                         placeholder="Stock"
                                                         className="w-20 text-center py-2 border-none focus:ring-0 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                         disabled={!selectedVariant || selectedVariantIsShopify}
                                                      />
                                                      <button
                                                          onClick={() => selectedVariant && handleAdjustStockClick(product.id, 1, selectedVariant.id)}
                                                          disabled={!selectedVariant || selectedVariantIsShopify}
                                                          className="px-3 py-2 border-l text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                       >
                                                          <PlusIcon className="h-4 w-4" />
                                                       </button>
                                                  </div>
                                              );
                                          })()}
                                          
                                          <button
                                              onClick={() => selectedVariant && handleSetStock(product.id, selectedVariant.id)}
                                              disabled={!selectedVariant || (selectedVariant && selectedVariant.external_id && product.external_source === 'shopify') || adjustmentQuantities[`variant-${selectedVariantId}`] === '' || isNaN(Number(adjustmentQuantities[`variant-${selectedVariantId}`]))}
                                              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                                            >
                                               Définir Stock à {adjustmentQuantities[`variant-${selectedVariantId}`] || ''}
                                          </button>
                                      </div>

                                      {selectedVariantId && (
                                          <div className="border-t border-gray-200 pt-4 mt-4">
                                              <h4 className="text-sm font-medium text-gray-700 mb-2">Configurer l'Alerte de Stock Bas</h4>
                                              {alertConfigs[selectedVariantId]?.loading && <p className="text-sm text-gray-500 italic">Chargement...</p>}
                                              {alertConfigs[selectedVariantId]?.error && <p className="text-sm text-red-600">Erreur: {alertConfigs[selectedVariantId].error}</p>}
                                              
                                              {!alertConfigs[selectedVariantId]?.loading && !alertConfigs[selectedVariantId]?.error && (
                                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                                      <div className="flex items-center">
                                                          <input
                                                              id={`alert-active-${selectedVariantId}`}
                                                              type="checkbox"
                                                              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                              checked={alertConfigs[selectedVariantId]?.isActive || false}
                                                              onChange={(e) => handleAlertConfigChange(selectedVariantId, 'isActive', e.target.checked)}
                                                          />
                                                          <label htmlFor={`alert-active-${selectedVariantId}`} className="ml-2 block text-sm text-gray-900">
                                                              Activer l'alerte
                                                          </label>
                                                      </div>

                                                      <div className="flex items-center">
                                                          <label htmlFor={`alert-threshold-${selectedVariantId}`} className="block text-sm font-medium text-gray-700 mr-2">
                                                              Seuil:
                                                          </label>
                                                          <input
                                                              type="number"
                                                              id={`alert-threshold-${selectedVariantId}`}
                                                              min="0"
                                                              className="w-20 block shadow-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                              value={alertConfigs[selectedVariantId]?.threshold ?? ''}
                                                              onChange={(e) => handleAlertConfigChange(selectedVariantId, 'threshold', e.target.value)}
                                                              disabled={!alertConfigs[selectedVariantId]?.isActive}
                                                              placeholder="Ex: 5"
                                                          />
                                                      </div>

                                                      <button
                                                          onClick={() => handleSaveAlertConfig(selectedVariantId)}
                                                          disabled={editingAlertVariantId === selectedVariantId || !alertConfigs[selectedVariantId] || alertConfigs[selectedVariantId].threshold === undefined || alertConfigs[selectedVariantId].threshold === '' || isNaN(Number(alertConfigs[selectedVariantId].threshold))}
                                                          className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                                                      >
                                                          {editingAlertVariantId === selectedVariantId ? 'Sauvegarde...' : 'Sauvegarder Alerte'}
                                                      </button>
                                                  </div>
                                              )}
                                          </div>
                                      )}
                                </div>
                            ) : product.variants && product.variants.length === 1 ? ( 
                                // ----- Cas avec UNE SEULE variation ----- 
                                (() => { 
                                    const singleVariant = product.variants[0]; 
                                    const singleVariantId = singleVariant.id; 
                                    const singleVariantIsShopify = singleVariant.external_id && product.external_source === 'shopify'; 
                                    const singleVariantAlertConfig = alertConfigs[singleVariantId]; 
                                    const singleVariantAdjValue = adjustmentQuantities[`variant-${singleVariantId}`] || '';
                                    
                                    return ( 
                                        <div className="space-y-4"> 
                                             {/* Affichage Nom Variation + Stock */} 
                                             <div className="font-medium text-gray-800"> 
                                                 Variation: {singleVariant.name} (Stock: {singleVariant.stock}) 
                                             </div> 
                                             
                                             {/* Ajustement Stock (MASQUÉ si Shopify) */} 
                                             {!singleVariantIsShopify && (
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2"> 
                                                    <span className="text-sm text-gray-600">Ajuster stock:</span> 
                                                    <div className="flex items-center border rounded-md"> 
                                                        <button onClick={() => handleAdjustStockClick(product.id, -1, singleVariantId)} disabled={singleVariantIsShopify} className="px-3 py-2 border-r text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"><MinusIcon className="h-4 w-4" /></button> 
                                                        <input type="number" value={singleVariantAdjValue} onChange={(e) => handleAdjustmentChange(`variant-${singleVariantId}`, e.target.value)} placeholder="Stock" className="w-20 text-center py-2 border-none focus:ring-0 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed" disabled={singleVariantIsShopify} /> 
                                                        <button onClick={() => handleAdjustStockClick(product.id, 1, singleVariantId)} disabled={singleVariantIsShopify} className="px-3 py-2 border-l text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"><PlusIcon className="h-4 w-4" /></button> 
                                                    </div> 
                                                    <button onClick={() => handleSetStock(product.id, singleVariantId)} disabled={singleVariantIsShopify || singleVariantAdjValue === '' || isNaN(Number(singleVariantAdjValue))} className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">Définir Stock à {singleVariantAdjValue}</button> 
                                                </div> 
                                            )}
  
                                             {/* Configuration des Alertes */} 
                                             <div className="border-t border-gray-200 pt-4 mt-4"> 
                                                 <h4 className="text-sm font-medium text-gray-700 mb-2">Configurer l'Alerte de Stock Bas</h4> 
                                                 {singleVariantAlertConfig?.loading && <p className="text-sm text-gray-500 italic">Chargement...</p>} 
                                                 {singleVariantAlertConfig?.error && <p className="text-sm text-red-600">Erreur: {singleVariantAlertConfig.error}</p>} 
                                                 {!singleVariantAlertConfig?.loading && !singleVariantAlertConfig?.error && ( 
                                                     <div className="flex flex-wrap items-center gap-x-4 gap-y-2"> 
                                                         <div className="flex items-center"> 
                                                             <input id={`alert-active-${singleVariantId}`} type="checkbox" className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" checked={singleVariantAlertConfig?.isActive || false} onChange={(e) => handleAlertConfigChange(singleVariantId, 'isActive', e.target.checked)} /> 
                                                             <label htmlFor={`alert-active-${singleVariantId}`} className="ml-2 block text-sm text-gray-900">Activer l'alerte</label> 
                                                         </div> 
                                                         <div className="flex items-center"> 
                                                             <label htmlFor={`alert-threshold-${singleVariantId}`} className="block text-sm font-medium text-gray-700 mr-2">Seuil:</label> 
                                                             <input type="number" id={`alert-threshold-${singleVariantId}`} min="0" className="w-20 block shadow-sm sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed" value={singleVariantAlertConfig?.threshold ?? ''} onChange={(e) => handleAlertConfigChange(singleVariantId, 'threshold', e.target.value)} disabled={!singleVariantAlertConfig?.isActive} placeholder="Ex: 5" /> 
                                                         </div> 
                                                         <button
                                                             onClick={() => handleSaveAlertConfig(singleVariantId)}
                                                             disabled={editingAlertVariantId === singleVariantId || !singleVariantAlertConfig || singleVariantAlertConfig.threshold === undefined || singleVariantAlertConfig.threshold === '' || isNaN(Number(singleVariantAlertConfig.threshold))}
                                                             className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                                                         >
                                                             {editingAlertVariantId === singleVariantId ? 'Sauvegarde...' : 'Sauvegarder Alerte'}
                                                         </button>
                                                     </div> 
                                                 )} 
                                             </div> 
                                        </div> 
                                    ); 
                                })() 
                            ) : ( 
                                // ----- Cas produit simple (0 variation) ----- 
                                // Garder le code existant pour l'ajustement du produit simple
                                // Note: Pas de section alerte ici car liée aux variantes
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2"> 
                                    <span className="text-sm text-gray-600">Stock actuel: {product.stockLevel}</span> 
                                   
                                    <div className={`flex items-center border rounded-md ${isShopifyManaged ? 'bg-gray-100 cursor-not-allowed' : ''}`}> 
                                       <button
                                           onClick={() => handleAdjustStockClick(product.id, -1)}
                                           disabled={isShopifyManaged}
                                           className="px-3 py-2 border-r text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                           <MinusIcon className="h-4 w-4" />
                                        </button>
                                        <input
                                           type="number"
                                           value={adjustmentQuantities[`product-${product.id}`] || ''}
                                           onChange={(e) => handleAdjustmentChange(`product-${product.id}`, e.target.value)}
                                           placeholder="Stock"
                                           className="w-20 text-center py-2 border-none focus:ring-0 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                                           disabled={isShopifyManaged}
                                       />
                                       <button
                                           onClick={() => handleAdjustStockClick(product.id, 1)}
                                           disabled={isShopifyManaged}
                                           className="px-3 py-2 border-l text-gray-500 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                           <PlusIcon className="h-4 w-4" />
                                        </button>
                                    </div> 
                                    
                                    <button
                                          onClick={() => handleSetStock(product.id)}
                                          disabled={isShopifyManaged || adjustmentQuantities[`product-${product.id}`] === '' || isNaN(Number(adjustmentQuantities[`product-${product.id}`]))}
                                          className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                                      >
                                          Définir Stock à {adjustmentQuantities[`product-${product.id}`] || ''}
                                    </button>
                                </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
              })}
            </tbody>
          </table>

          {/* Message si aucun produit trouvé */}
          {sortedProducts.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p>Aucun produit trouvé correspondant à vos critères.</p>
            </div>
          )}

          {/* Contrôles de Pagination */}
          {totalPages > 0 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isSyncingAll}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || isSyncingAll}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> sur <span className="font-medium">{totalPages}</span> ({totalProducts} produits)
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || isSyncingAll}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Précédent</span>
                      <ChevronDownIcon className="h-5 w-5 transform rotate-90" aria-hidden="true" />
                    </button>
                    {/* Potentiellement ajouter des numéros de page ici */}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || isSyncingAll}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Suivant</span>
                      <ChevronUpIcon className="h-5 w-5 transform rotate-90" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Overlay de blocage pendant la synchronisation */}
      {isSyncingAll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Synchronisation en cours...
            </h3>
            <p className="text-gray-600">
              Récupération et traitement des produits Shopify.
              <br />
              Veuillez patienter, cela peut prendre quelques minutes.
            </p>
            <div className="mt-4 text-sm text-gray-500">
              ⚠️ Ne fermez pas cette page pendant la synchronisation
            </div>
          </div>
        </div>
      )}

      {/* Modal de résultats de synchronisation */}
      {showSyncModal && syncResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* En-tête du modal */}
            <div className={`px-6 py-4 border-b ${syncResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {syncResults.success ? (
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )}
                  <h3 className={`ml-3 text-lg font-semibold ${syncResults.success ? 'text-green-900' : 'text-red-900'}`}>
                    {syncResults.success ? 'Synchronisation réussie !' : 'Erreur de synchronisation'}
                  </h3>
                </div>
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contenu du modal */}
            <div className="px-6 py-4">
              {syncResults.success && syncResults.data ? (
                <div className="space-y-6">
                  {/* Résumé principal */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {syncResults.data.totalProductsFound || 0}
                      </div>
                      <div className="text-sm text-blue-800">Produits trouvés</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {syncResults.data.productsCreated || 0}
                      </div>
                      <div className="text-sm text-green-800">Produits créés</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-600">
                        {syncResults.data.productsUpdated || 0}
                      </div>
                      <div className="text-sm text-yellow-800">Produits mis à jour</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {syncResults.data.pagesProcessed || 0}
                      </div>
                      <div className="text-sm text-purple-800">Batches traités</div>
                    </div>
                  </div>

                  {/* Détails des variantes */}
                  {(syncResults.data.variantsCreated > 0 || syncResults.data.variantsUpdated > 0) && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Variantes</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-green-600 font-medium">{syncResults.data.variantsCreated || 0}</span>
                          <span className="text-gray-600"> variantes créées</span>
                        </div>
                        <div>
                          <span className="text-blue-600 font-medium">{syncResults.data.variantsUpdated || 0}</span>
                          <span className="text-gray-600"> variantes mises à jour</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Erreurs */}
                  {syncResults.data.errors && syncResults.data.errors.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="font-semibold text-red-900 mb-2">
                        ⚠️ Erreurs ({syncResults.data.errors.length})
                      </h4>
                      <div className="max-h-32 overflow-y-auto">
                        {syncResults.data.errors.map((error, index) => (
                          <div key={index} className="text-sm text-red-700 mb-1">
                            • {error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Avertissements */}
                  {syncResults.data.warnings && syncResults.data.warnings.length > 0 && (
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-900 mb-2">
                        ⚠️ Avertissements ({syncResults.data.warnings.length})
                      </h4>
                      <div className="max-h-32 overflow-y-auto">
                        {syncResults.data.warnings.map((warning, index) => (
                          <div key={index} className="text-sm text-yellow-700 mb-1">
                            • {warning}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-red-600">{syncResults.message}</p>
                </div>
              )}
            </div>

            {/* Pied du modal */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StockManagement; 